import { useRef, useState } from 'react'

import { Button } from '@/components/Button'
import { sendEmail } from '@/lib/utils'

type T = {}

type SubmissionState = 'idle' | 'submitting' | 'success' | 'error'

export const ContactForm = ({}: T) => {
  const formRef = useRef<HTMLFormElement>(null)
  const [inquiryType, setInquiryType] = useState<string>('tax-return')
  const [submissionState, setSubmissionState] =
    useState<SubmissionState>('idle')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSubmissionState('submitting')
    setErrorMessage('')

    try {
      const formData = new FormData(e.currentTarget)
      formData.append('inquiryType', inquiryType)

      const result = await sendEmail(e)

      if (result?.success) {
        setSubmissionState('success')
        // フォームをリセット
        if (formRef.current) {
          formRef.current.reset()
          setInquiryType('tax-return')
        }
      } else {
        throw new Error(result?.error || '送信に失敗しました')
      }
    } catch (error) {
      setSubmissionState('error')
      setErrorMessage(
        error instanceof Error
          ? error.message
          : '送信に失敗しました。しばらく時間をおいて再度お試しください。',
      )
    }
  }

  const resetForm = () => {
    setSubmissionState('idle')
    setErrorMessage('')
  }

  return (
    <div className="mx-auto mt-8 max-w-5xl text-left md:mt-20">
      {/* 成功メッセージ */}
      {submissionState === 'success' && (
        <div className="rounded-lg border p-6 bg-primary-5 border-primary-20">
          <div className="flex">
            <span className="material-symbols-outlined mr-3 text-primary">
              check_circle
            </span>
            <div>
              <h3 className="mt-1 font-semibold text-primary md:mt-0.75 md:text-lg">
                送信完了
              </h3>
              <p className="mt-fz-1 text-sm leading-normal text-body-65">
                お問い合わせありがとうございます。内容を確認の上、5営業日以内にご連絡いたします。
              </p>
              <button
                onClick={resetForm}
                className="mt-3 text-sm underline text-body-65 hover:no-underline"
              >
                新しいお問い合わせを送信する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* エラーメッセージ */}
      {submissionState === 'error' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center">
            <span className="material-symbols-outlined mr-3 text-red-600">
              error
            </span>
            <div>
              <h3 className="mt-1 font-semibold text-red-800 md:mt-0.75 md:text-lg">
                送信エラー
              </h3>
              <p className="mt-fz-1 text-sm leading-normal text-body-65">
                {errorMessage}
              </p>
              <button
                onClick={resetForm}
                className="mt-3 text-sm underline text-body-65 hover:no-underline"
              >
                再度送信する
              </button>
            </div>
          </div>
        </div>
      )}

      {(submissionState === 'idle' || submissionState === 'submitting') && (
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="grid grid-cols-2 gap-x-lg gap-y-6 border-t pt-6 border-body-25 md:gap-x-12 md:border-none md:pt-0"
        >
          {/* ラジオボタンエリア */}
          <fieldset className="col-span-2 flex gap-x-12 md:border-b md:pb-5 md:border-body-25">
            <legend className="text-sm font-semibold md:contents">
              <span>
                お問合せ種別<span className="pl-1 text-secondary">*</span>
              </span>
            </legend>
            <div className="ml-1 mt-4 grid gap-x-8 gap-y-fz-1 text-sm md:mt-0 md:flex">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="inquiryType"
                  value="tax-return"
                  checked={inquiryType === 'tax-return'}
                  onChange={() => setInquiryType('tax-return')}
                  className="size-2 appearance-none rounded-full outline outline-1 outline-offset-2 outline-body checked:bg-primary"
                />
                <span className="ml-2">暗号資産の税務顧問</span>
              </label>

              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="inquiryType"
                  value="tax-advisor"
                  checked={inquiryType === 'tax-advisor'}
                  onChange={() => setInquiryType('tax-advisor')}
                  className="size-2 appearance-none rounded-full outline outline-1 outline-offset-2 outline-body checked:bg-primary"
                />
                <span className="ml-2">企業向けの税務顧問</span>
              </label>

              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="inquiryType"
                  value="other"
                  checked={inquiryType === 'other'}
                  onChange={() => setInquiryType('other')}
                  className="size-2 appearance-none rounded-full outline outline-1 outline-offset-2 outline-body checked:bg-primary"
                />
                <span className="ml-2">その他</span>
              </label>
            </div>
          </fieldset>

          {/* お名前 */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold">
              お名前<span className="pl-1 text-secondary">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder="堀口 会計"
              className="mt-3 w-full rounded border p-3 border-body-25 placeholder:text-body-25 focus-visible:outline-body-50"
              required
            />
          </div>

          {/* 会社名・組織名 */}
          <div>
            <label htmlFor="company" className="block text-sm font-semibold">
              会社名・組織名
              {inquiryType === 'tax-advisor' && (
                <span className="pl-1 text-secondary">*</span>
              )}
            </label>
            <input
              type="text"
              id="company"
              name="company"
              placeholder="税理士法人 堀口会計"
              className="mt-3 w-full rounded border p-3 border-body-25 placeholder:text-body-25 focus-visible:outline-body-50"
              required={inquiryType === 'tax-advisor'}
            />
          </div>

          {/* メールアドレス */}
          <div>
            <label htmlFor="email" className="block text-sm font-semibold">
              メールアドレス<span className="pl-1 text-secondary">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="info@h-tax.or.jp"
              className="mt-3 w-full rounded border p-3 border-body-25 placeholder:text-body-25 focus-visible:outline-body-50"
              required
            />
          </div>

          {/* 電話番号 */}
          <div>
            <label htmlFor="tel" className="block text-sm font-semibold">
              電話番号<span className="pl-1 text-secondary">*</span>
            </label>
            <input
              type="tel"
              id="tel"
              name="tel"
              placeholder="000-0000-0000"
              className="mt-3 w-full rounded border p-3 border-body-25 placeholder:text-body-25 focus-visible:outline-body-50"
              required
            />
          </div>

          {/* お問い合わせ内容（テキストエリア） */}
          <div className="col-span-2">
            <label htmlFor="message" className="block text-sm font-semibold">
              お問い合わせ内容<span className="pl-1 text-secondary">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows={10}
              placeholder=""
              className="mt-3 w-full rounded border p-3 leading-normal border-body-25 placeholder:text-body-25 focus-visible:outline-body-50"
              required
            />
          </div>

          {/* 送信ボタン */}
          <div className="col-span-2 text-center text-sm md:text-base">
            <Button
              text={submissionState === 'submitting' ? '送信中...' : '送信する'}
              type="submit"
              disabled={submissionState === 'submitting'}
            />
            {submissionState === 'submitting' && (
              <p className="mt-fz-0.75 text-xs text-body-65">
                送信中です。しばらくお待ちください...
              </p>
            )}
          </div>
        </form>
      )}
    </div>
  )
}
